import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  GraduationCap,
  History,
  Home,
  Inbox,
  LayoutDashboard,
  LifeBuoy,
  ListOrdered,
  MessagesSquare,
  Tags,
  type LucideIcon,
} from "lucide-react";
import { canAccessModuleRoute, hasAnyPermission, type ModuleKey } from "@/lib/module-access";

export type HubKey = "utility" | "community";

/** Same shape as a Sidebar item, so module menus render in the shared sidebar. */
export type HubNavItem = { name: string; path: string; icon: LucideIcon; permission?: string; module?: string };
export type HubNavGroup = { title: string; items: HubNavItem[] };

export type HubModule = {
  key: string;
  hub: HubKey;
  name: string;
  description: string;
  icon: LucideIcon;
  /** Icon tile colours: each module keeps its own so tiles are told apart at a glance. */
  tone: string;
  href: string;
  category: string;
  status: "live" | "beta" | "soon";
  /** Path prefixes that belong to this module; the sidebar shows its menu while inside one. */
  paths: string[];
  nav: HubNavGroup[];
  /** Who sees the tile. Without it every signed-in user does. */
  access?: { module?: ModuleKey; anyPermission?: string[] };
};

export type HubAccess = { allowedModules: string[]; permissions: string[] };

export const HUBS: Record<HubKey, { name: string; home: string; categories: { key: string; label: string }[] }> = {
  utility: {
    name: "Utility",
    home: "/utility",
    categories: [
      { key: "support", label: "IT & Support" },
      { key: "data", label: "Data & Reports" },
    ],
  },
  community: {
    name: "Community",
    home: "/community",
    categories: [
      { key: "learn", label: "Learning" },
      { key: "discuss", label: "Discussion" },
    ],
  },
};

/**
 * Every module of the Utility and Community hubs. A new module is one entry here: the hub home
 * (launcher) and the sidebar both read this list.
 */
export const HUB_MODULES: HubModule[] = [
  {
    key: "helpdesk",
    hub: "utility",
    name: "IT Helpdesk",
    description: "Report IT problems and follow them until they are solved.",
    icon: LifeBuoy,
    tone: "bg-orange-50 text-[#e5722a]",
    href: "/utility/helpdesk",
    category: "support",
    status: "live",
    paths: ["/utility/helpdesk"],
    nav: [
      {
        title: "IT HELPDESK",
        items: [
          { name: "My tickets", path: "/utility/helpdesk", icon: LifeBuoy },
          { name: "Queue", path: "/utility/helpdesk/queue", icon: ListOrdered },
        ],
      },
      {
        title: "IT DESK",
        items: [
          { name: "Inbox", path: "/utility/helpdesk/desk", icon: Inbox, permission: "utility.helpdesk.tickets.view" },
          { name: "Dashboard", path: "/utility/helpdesk/desk/dashboard", icon: LayoutDashboard, permission: "utility.helpdesk.dashboard.view" },
          { name: "Topics", path: "/utility/helpdesk/desk/settings", icon: Tags, permission: "utility.helpdesk.settings.manage" },
          { name: "Audit Log", path: "/utility/helpdesk/desk/audit-logs", icon: History, permission: "utility.helpdesk.audit.view" },
        ],
      },
    ],
  },
  {
    key: "dashly",
    hub: "utility",
    name: "Dashly",
    description: "Turn Google Sheets into reports and share them privately.",
    icon: BarChart3,
    tone: "bg-teal-50 text-teal-600",
    href: "/insight",
    category: "data",
    status: "live",
    // Dashly keeps its own sidebar under /insight.
    paths: [],
    nav: [],
    access: { module: "insight" },
  },
  {
    key: "manuals",
    hub: "community",
    name: "Manual Book",
    description: "Step-by-step user manuals for the AiBAS modules.",
    icon: BookOpen,
    tone: "bg-amber-50 text-amber-600",
    href: "/community/manuals",
    category: "learn",
    status: "live",
    paths: ["/community/manuals"],
    nav: [{ title: "MANUAL BOOK", items: [{ name: "All manuals", path: "/community/manuals", icon: BookOpen }] }],
  },
  {
    key: "academy",
    hub: "community",
    name: "AiBAS Academy",
    description: "Short lessons and onboarding paths, module by module.",
    icon: GraduationCap,
    tone: "bg-emerald-50 text-emerald-600",
    href: "/community/academy",
    category: "learn",
    status: "soon",
    paths: ["/community/academy"],
    nav: [{ title: "AIBAS ACADEMY", items: [{ name: "Overview", path: "/community/academy", icon: GraduationCap }] }],
  },
  {
    key: "qna",
    hub: "community",
    name: "Q&A",
    description: "Ask how something works and get answers from colleagues.",
    icon: MessagesSquare,
    tone: "bg-violet-50 text-violet-600",
    href: "/community/qna",
    category: "discuss",
    status: "soon",
    paths: ["/community/qna"],
    nav: [{ title: "Q&A", items: [{ name: "Overview", path: "/community/qna", icon: MessagesSquare }] }],
  },
];

export function canSeeModule(module: HubModule, access: HubAccess): boolean {
  if (!module.access) return true;
  if (module.access.module && !canAccessModuleRoute(module.access.module, access.allowedModules, access.permissions)) return false;
  if (module.access.anyPermission && !hasAnyPermission(access.permissions, module.access.anyPermission)) return false;
  return true;
}

export function hubModules(hub: HubKey, access: HubAccess): HubModule[] {
  return HUB_MODULES.filter((module) => module.hub === hub && canSeeModule(module, access));
}

export function moduleForPath(hub: HubKey, pathname: string): HubModule | undefined {
  return HUB_MODULES.find((module) => module.hub === hub && module.paths.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)));
}

/**
 * Contextual sidebar: the hub home lists the modules; inside a module only its own menu shows,
 * under a link back to the hub.
 */
export function hubSidebarGroups(hub: HubKey, pathname: string, access: HubAccess): HubNavGroup[] {
  const { name, home } = HUBS[hub];
  const current = moduleForPath(hub, pathname);
  if (current) {
    return [{ title: name.toUpperCase(), items: [{ name: `Back to ${name}`, path: home, icon: ArrowLeft }] }, ...current.nav];
  }
  return [
    { title: name.toUpperCase(), items: [{ name: "Home", path: home, icon: Home }] },
    { title: "MODULES", items: hubModules(hub, access).map((module) => ({ name: module.name, path: module.href, icon: module.icon })) },
  ];
}
